/**
 * Outil « Tableau d'avancement » (Boîte à outils, physique-chimie 2nde → Tle).
 *
 * Explorer : une réaction, les quantités initiales des réactifs ; le curseur
 * fait avancer la transformation (x de 0 à x_max) : le tableau d'avancement
 * se remplit (état initial, en cours, final) et des jauges se vident ou se
 * remplissent. Le réactif limitant se trouve en comparant les n₀/ν, avec le
 * raisonnement écrit ; un bouton rend le mélange stœchiométrique. En Tle :
 * transformation non totale, x_f = τ x_max. Remplir : l'élève trouve le
 * réactif limitant, x_max et l'état final, puis vérifie. Défi : cinq
 * questions. Logique : `progress.ts`.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import {
  Note,
  NumberField,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import {
  REACTIONS,
  amountsAt,
  eq,
  equationSpoken,
  equationText,
  exprText,
  fmt,
  limitLines,
  limitOf,
  makeProblem,
  makeQuiz,
  near,
  parseAmount,
  qty,
  quizKinds,
  reactionById,
  stoichPartner,
  tracked,
  type Problem,
  type Quiz,
  type Reaction,
  type Term,
} from './progress';

const SELECT =
  'border-ink-200 text-ink-900 min-h-11 w-full rounded-xl border bg-white px-2 text-[15px]';
const NB = String.fromCharCode(0xa0);

/** une équation : formules à l'écran, mots pour le lecteur d'écran */
function Equation({ r, className }: { r: Reaction; className?: string }) {
  return (
    <p className={cn('text-ink-900 text-base font-semibold', className)}>
      <span aria-hidden="true">{frenchSpaces(equationText(r))}</span>
      <span className="sr-only">{equationSpoken(r)}</span>
    </p>
  );
}

/** texte d'un outil : « x_max », « x_f » écrits en indice, espaces françaises */
function Rich({ text }: { text: string }) {
  return (
    <>
      {frenchSpaces(text)
        .split(/(x_max|x_f)/)
        .map((part, i) =>
          part === 'x_max' || part === 'x_f' ? (
            <span key={i}>
              x<sub className="text-[12px]">{part.slice(2)}</sub>
            </span>
          ) : (
            part
          ),
        )}
    </>
  );
}

const header = (x: Term) => `${x.nu === 1 ? '' : `${x.nu}${NB}`}${x.sp.text}`;

/* ---------- tableau d'avancement ---------- */

interface TableProps {
  r: Reaction;
  n0: readonly number[];
  /** avancement de la ligne « en cours » ('expr' : les expressions seules) */
  x: number | 'expr';
  /** avancement final (x_max, ou x_f en Tle) */
  xEnd: number;
  /** x_f < x_max : on écrit x_f */
  partial?: boolean;
  /** cases de l'état final à masquer (exercice) */
  hideFinal?: boolean;
}

function ProgressTable({ r, n0, x, xEnd, partial = false, hideFinal = false }: TableProps) {
  const terms = [...r.reactants, ...r.products];
  const rs = tracked(r.reactants);
  const L = limitOf(r, n0);
  const u = r.unit;
  // n₀ de chaque espèce suivie, dans l'ordre de `terms` (produits : 0)
  const n0Of = (t2: Term) => {
    const i = rs.indexOf(t2);
    return i >= 0 ? (n0[i] ?? 0) : 0;
  };
  const isReactant = (t2: Term) => r.reactants.includes(t2);
  const at = (t2: Term, xx: number) =>
    isReactant(t2) ? Math.max(0, n0Of(t2) - t2.nu * xx) : t2.nu * xx;
  const CELL = 'border-ink-100 border px-1.5 py-1.5 text-center tabular-nums';
  const cell = (t2: Term, i: number, content: string, strong = false) => (
    <td key={i} className={cn(CELL, strong && 'text-success-600 font-bold')}>
      {frenchSpaces(content)}
    </td>
  );
  const role = (t2: Term) => (t2.role ? t2.role : null);
  const xName = partial ? 'x_f' : 'x_max';
  // l'état et son avancement dans la même colonne : le tableau tient sur un téléphone
  const rowHead = (state: string, xText: string) => (
    <th scope="row" className="border-ink-100 border px-1.5 py-1.5 text-left">
      <span className="block font-semibold">{state}</span>
      <span className="text-ink-600 block text-sm font-normal">
        <Rich text={xText} />
      </span>
    </th>
  );
  return (
    <div
      className="focus-visible:ring-primary-500 overflow-x-auto outline-none focus-visible:ring-2"
      data-testid="rp-table"
      // le tableau peut défiler : il se fait atteindre au clavier
      tabIndex={0}
      role="region"
      aria-label="Tableau d’avancement (défile horizontalement)"
    >
      <table className="text-ink-800 w-full min-w-max border-collapse text-sm">
        <caption className="text-ink-500 pb-1 text-left text-xs">
          {frenchSpaces(`Tableau d’avancement — quantités de matière en ${u}`)}
        </caption>
        <thead>
          <tr className="bg-ink-50">
            <th scope="col" className="border-ink-100 border px-1.5 py-1.5 text-left">
              <span className="block">État</span>
              <span className="text-ink-600 block text-xs font-normal">
                {frenchSpaces(`x en ${u}`)}
              </span>
            </th>
            {terms.map((t2, i) => (
              <th key={i} scope="col" className="border-ink-100 border px-1.5 py-1.5 font-bold">
                <span aria-hidden="true">{frenchSpaces(header(t2))}</span>
                <span className="sr-only">{`${t2.nu === 1 ? '' : `${t2.nu} `}${t2.sp.name}`}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {rowHead('Initial', 'x = 0')}
            {terms.map((t2, i) => cell(t2, i, role(t2) ?? fmt(n0Of(t2))))}
          </tr>
          <tr>
            {rowHead('En cours', x === 'expr' ? 'x' : `x = ${fmt(x)}`)}
            {terms.map((t2, i) =>
              role(t2) ? (
                cell(t2, i, role(t2)!)
              ) : (
                <td key={i} className={CELL}>
                  <span className={cn('block', x !== 'expr' && 'text-ink-500 text-xs')}>
                    {frenchSpaces(exprText(n0Of(t2), t2.nu, isReactant(t2)))}
                  </span>
                  {x !== 'expr' && fmt(at(t2, x))}
                </td>
              ),
            )}
          </tr>
          <tr className="bg-ink-50">
            {rowHead('Final', hideFinal ? `${xName} = ?` : `${xName} ${eq(xEnd)}`)}
            {terms.map((t2, i) => {
              if (role(t2)) return cell(t2, i, role(t2)!);
              if (hideFinal) return cell(t2, i, '?');
              const v = at(t2, xEnd);
              const lim = !partial && isReactant(t2) && L.limiting.includes(rs.indexOf(t2));
              return cell(t2, i, fmt(v), lim);
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** jauges : la quantité de chaque espèce suivie à l'avancement x */
function Gauges({
  r,
  n0,
  x,
  xEnd,
}: {
  r: Reaction;
  n0: readonly number[];
  x: number;
  xEnd: number;
}) {
  const rs = tracked(r.reactants);
  const ps = tracked(r.products);
  const now = amountsAt(r, n0, x);
  const end = amountsAt(r, n0, xEnd);
  const top = Math.max(1e-9, ...n0, ...end);
  const all = [...rs, ...ps];
  return (
    <div
      className="border-ink-100 flex items-end justify-around gap-2 rounded-2xl border px-2 pb-2 pt-3"
      aria-hidden="true"
      data-testid="rp-gauges"
    >
      {all.map((t2, i) => {
        const v = now[i]!;
        const reactant = i < rs.length;
        return (
          <div key={t2.sp.text} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <span className="text-ink-700 text-xs tabular-nums">{fmt(v)}</span>
            <div className="bg-ink-100 relative h-28 w-full max-w-12 overflow-hidden rounded-lg">
              <div
                className={cn(
                  'absolute bottom-0 w-full rounded-lg transition-[height]',
                  reactant ? 'bg-primary-500' : 'bg-success-600',
                )}
                style={{ height: `${(100 * v) / top}%` }}
              />
            </div>
            <span className="text-ink-900 text-sm font-semibold">{t2.sp.text}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- explorer ---------- */

/**
 * `partialOk` : transformations totales ET non totales (1re, BO : avancement
 * final, avancement maximal) ; `tle` : le taux d'avancement final τ.
 */
function Explore({ partialOk, tle }: { partialOk: boolean; tle: boolean }) {
  const reactions = REACTIONS.filter((r) => partialOk || !r.tau);
  const [id, setId] = useState(reactions[0]!.id);
  const r = reactionById(id);
  const rs = tracked(r.reactants);
  const [n0, setN0] = useState<number[]>(() => [...r.init]);
  const [frac, setFrac] = useState(1);
  const [partial, setPartial] = useState(false);
  const [tau, setTau] = useState(0.5);
  const selectId = useId();

  const L = limitOf(r, n0);
  const nonTotal = partialOk && partial;
  const xEnd = nonTotal ? tau * L.xmax : L.xmax;
  const x = frac * xEnd;
  const lines = limitLines(r, n0, !nonTotal);

  const choose = (nid: string) => {
    const nr = reactionById(nid);
    setId(nid);
    setN0([...nr.init]);
    setFrac(1);
    // une réaction totale repart en transformation totale
    setPartial(!!nr.tau);
    setTau(nr.tau ?? 0.5);
  };
  const [A, B] = rs;
  const partner = A && B ? stoichPartner(n0[0] ?? 0, A.nu, B.nu) : null;
  // le bouton n'apparaît que si la valeur à 2 décimales donne VRAIMENT un mélange stœchiométrique
  const partnerOk =
    partner !== null && partner <= 20 && Math.abs(Number(partner.toFixed(2)) - partner) < 1e-9;

  return (
    <div className="space-y-3">
      <label htmlFor={selectId} className="block space-y-1">
        <span className="text-ink-600 text-xs font-semibold">Réaction</span>
        <select
          id={selectId}
          className={SELECT}
          value={id}
          onChange={(e) => choose(e.target.value)}
        >
          {reactions.map((x2) => (
            <option key={x2.id} value={x2.id}>
              {x2.label}
            </option>
          ))}
        </select>
      </label>
      <Equation r={r} />
      {r.note && <p className="text-ink-600 text-sm">{frenchSpaces(r.note)}</p>}
      <div className={cn('grid gap-2', rs.length > 1 && 'grid-cols-2')}>
        {rs.map((t2, i) => (
          <NumberField
            key={`${r.id}-${t2.sp.text}`}
            label={`n₀(${t2.sp.text}) en ${r.unit}`}
            value={n0[i] ?? 0}
            min={0.5}
            max={20}
            step={0.5}
            decimals={2}
            onChange={(v) => {
              const next = [...n0];
              next[i] = v;
              setN0(next);
            }}
          />
        ))}
      </div>
      {partnerOk && !L.stoichiometric && (
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => setN0([n0[0] ?? 0, Number(partner!.toFixed(2)), ...n0.slice(2)])}
        >
          {frenchSpaces(`Mélange stœchiométrique : n₀(${B!.sp.text}) = ${qty(partner!, r.unit)}`)}
        </button>
      )}
      {partialOk && (
        <div className="space-y-2">
          <ToggleChip on={partial} onClick={() => setPartial(!partial)}>
            Transformation non totale
          </ToggleChip>
          {/* τ = 1 serait une transformation totale : le curseur s'arrête avant */}
          {partial &&
            (tle ? (
              <ParamSlider
                name="Taux d’avancement final τ"
                ariaLabel="Taux d’avancement final tau"
                value={tau}
                min={0.01}
                max={0.99}
                step={0.01}
                format={(v) => `${fmt(v * 100)}${String.fromCharCode(0x202f)}%`}
                onChange={setTau}
              />
            ) : (
              <ParamSlider
                name="Avancement final"
                ariaLabel="Avancement final x f"
                value={tau}
                min={0.01}
                max={0.99}
                step={0.01}
                format={(v) =>
                  frenchSpaces(`x_f = ${fmt(v * L.xmax)}${NB}${r.unit}`).replace('x_f', 'xf')
                }
                onChange={setTau}
              />
            ))}
        </div>
      )}
      <ParamSlider
        name="Avancement x"
        ariaLabel="Avancement x"
        value={frac}
        min={0}
        max={1}
        step={0.02}
        format={(v) => frenchSpaces(`x = ${fmt(v * xEnd)}${NB}${r.unit}`)}
        onChange={setFrac}
      />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={SECONDARY_BUTTON} onClick={() => setFrac(0)}>
          État initial
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={() => setFrac(1)}>
          État final
        </button>
      </div>
      <Gauges r={r} n0={n0} x={x} xEnd={xEnd} />
      <ProgressTable r={r} n0={n0} x={x} xEnd={xEnd} partial={nonTotal} />
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="rp-line" className="mb-1 block last:mb-0">
            <Rich text={l} />
          </span>
        ))}
        {nonTotal && (
          <span className="mb-1 block last:mb-0" data-testid="rp-line">
            <Rich
              text={
                tle
                  ? `Transformation non totale : x_f = τ × x_max ${eq(xEnd)}${NB}${r.unit} (τ = ${fmt(tau)}). À l’état final, tous les réactifs sont encore présents.`
                  : `Transformation non totale : elle s’arrête à x_f ${eq(xEnd)}${NB}${r.unit}, avant x_max. À l’état final, tous les réactifs sont encore présents.`
              }
            />
          </span>
        )}
      </Note>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(lines.at(-1) ?? '')
          .replace(/x_max/g, 'x max')
          .replace(/x_f/g, 'x f')}
      </p>
    </div>
  );
}

/* ---------- remplir l'état final ---------- */

type Answers = { lim: string; xmax: string; fin: string[] };

function Fill({ seed }: { seed?: number }) {
  const rng = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [p, setP] = useState<Problem>(() => makeProblem(rng));
  const [a, setA] = useState<Answers>({ lim: '', xmax: '', fin: [] });
  const [checked, setChecked] = useState(false);
  const [shown, setShown] = useState(false);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [p]);

  const { r, n0 } = p;
  const rs = tracked(r.reactants);
  const ps = tracked(r.products);
  const all = [...rs, ...ps];
  const L = limitOf(r, n0);
  const fin = amountsAt(r, n0, L.xmax);
  const limText = L.stoichiometric ? 'stœchiométrique' : rs[L.limiting[0]!]!.sp.text;
  const ok = {
    lim: a.lim === limText,
    xmax: (() => {
      const v = parseAmount(a.xmax);
      return v !== null && near(v, L.xmax);
    })(),
    fin: all.map((_, i) => {
      const v = parseAmount(a.fin[i] ?? '');
      return v !== null && near(v, fin[i]!);
    }),
  };
  const score = [ok.lim, ok.xmax, ...ok.fin].filter(Boolean).length;
  const total = 2 + all.length;
  const next = () => {
    moved.current = true;
    setP(makeProblem(rng));
    setA({ lim: '', xmax: '', fin: [] });
    setChecked(false);
    setShown(false);
  };
  const mark = (good: boolean) =>
    checked ? (good ? 'border-success-600 bg-success-50' : 'border-error-500 bg-error-50') : '';
  const INPUT =
    'border-ink-200 text-ink-900 min-h-11 w-24 rounded-xl border bg-white px-2 text-center text-[15px] tabular-nums';

  return (
    <div className="space-y-3">
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-sm font-semibold outline-none">
        <Rich
          text={`État initial : ${rs.map((x2, i) => `n₀(${x2.sp.text}) = ${qty(n0[i]!, r.unit)}`).join(', ')}, aucun produit. Trouve le réactif limitant, x_max et l’état final.`}
        />
      </p>
      <Equation r={r} />
      <ProgressTable r={r} n0={n0} x="expr" xEnd={L.xmax} hideFinal={!shown} />
      <div role="group" aria-label="Réactif limitant" className="space-y-1">
        <p className="text-ink-700 text-sm font-semibold">Réactif limitant</p>
        <div className="flex flex-wrap gap-2">
          {[...rs.map((x2) => x2.sp.text), 'stœchiométrique'].map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={a.lim === v}
              onClick={() => {
                setA({ ...a, lim: v });
                setChecked(false);
              }}
              className={cn(
                'min-h-11 min-w-11 rounded-xl border px-3 text-sm font-semibold',
                a.lim === v
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-ink-200 text-ink-700',
                a.lim === v && mark(ok.lim),
              )}
            >
              {v === 'stœchiométrique' ? 'Mélange stœchiométrique' : v}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <label className="space-y-1">
          <span className="text-ink-700 block text-sm font-semibold">
            <Rich text={`x_max (${r.unit})`} />
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={a.xmax}
            aria-label={`x max (${r.unit})`}
            onChange={(e) => {
              setA({ ...a, xmax: e.target.value });
              setChecked(false);
            }}
            className={cn(INPUT, mark(ok.xmax))}
          />
        </label>
        {all.map((t2, i) => (
          <label key={t2.sp.text} className="space-y-1">
            <span className="text-ink-700 block text-sm font-semibold">
              <span aria-hidden="true">{frenchSpaces(`n(${t2.sp.text}) final`)}</span>
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={a.fin[i] ?? ''}
              // le nom lu commence par le libellé visible (« n(H₂O) final »)
              aria-label={`n(${t2.sp.text}) final : quantité ${/^[aeiouhéè]/i.test(t2.sp.name) ? `d’${t2.sp.name}` : `de ${t2.sp.name}`} à l’état final, en ${r.unit}`}
              onChange={(e) => {
                const f = [...a.fin];
                f[i] = e.target.value;
                setA({ ...a, fin: f });
                setChecked(false);
              }}
              className={cn(INPUT, mark(ok.fin[i]!))}
            />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={PRIMARY_BUTTON} onClick={() => setChecked(true)}>
          Vérifier
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={() => setShown(true)}>
          Voir la correction
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={next}>
          Nouvel exercice
        </button>
      </div>
      <div aria-live="polite">
        {checked && (
          <Note live={false} tone={score === total ? 'success' : 'error'}>
            <span data-testid="rp-score">
              <Rich
                text={
                  score === total
                    ? `Tout est juste (${score} / ${total}) !`
                    : `${score} / ${total} juste${score > 1 ? 's' : ''}. ${ok.lim ? '' : 'Divise chaque n₀ par son coefficient et compare. '}${ok.xmax || !ok.lim ? '' : 'x_max annule la quantité du réactif limitant. '}Les cases en rouge sont à revoir.`
                }
              />
            </span>
          </Note>
        )}
      </div>
      {/* la correction est annoncée quand elle apparaît */}
      <div aria-live="polite">
        {shown && (
          <Note live={false}>
            {limitLines(r, n0).map((l) => (
              <span key={l} className="mb-1 block last:mb-0" data-testid="rp-correction">
                <Rich text={l} />
              </span>
            ))}
            <span className="block">
              <Rich
                text={`État final : ${all.map((t2, i) => `n(${t2.sp.text}) = ${exprText(i < rs.length ? n0[i]! : 0, t2.nu, i < rs.length).replace('x', 'x_max')} ${eq(fin[i]!)}${NB}${r.unit}`).join(' ; ')}.`}
              />
            </span>
          </Note>
        )}
      </div>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Écris les quantités avec une virgule (1,5) ; une valeur à 1 % près est acceptée.',
        )}
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed, rank }: { seed?: number; rank: number | null }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const kinds = quizKinds(rank);
  const rounds = kinds.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(kinds[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(kinds[c - 1]!, rnd));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p className="text-ink-900 text-base font-semibold">
        <span aria-hidden="true">{frenchSpaces(q.equation)}</span>
        <span className="sr-only">{q.equationSpoken}</span>
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base outline-none">
        <Rich text={q.question} />
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            <Rich text={o.text} />
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="rp-feedback">
              <Rich
                text={
                  chosen.correct
                    ? `Exact. ${q.explain}`
                    : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`
                }
              />
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function ReactionProgressTool({
  level = null,
  seed,
}: {
  /** niveau : la transformation non totale (τ) n'apparaît qu'en Tle (ou niveau inconnu) */
  level?: string | null;
  /** graine des exercices (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  // 1re : transformations non totales (x_f) ; Tle : τ ; niveau inconnu : tout
  const partialOk = rank === null || rank >= 6;
  const tle = rank === null || rank >= 7;
  const [mode, setMode] = useState<'explore' | 'fill' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'fill', label: 'Remplir' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore partialOk={partialOk} tle={tle} />
      </div>
      <div hidden={mode !== 'fill'}>
        <Fill seed={seed} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} rank={rank} />
      </div>
    </div>
  );
}
